import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast-service';
import { SystemIntegrationTypeService, IntegrationType, SaveIntegrationTypeRequest } from '../../../services/system-integration-type.service';
import { GridColumn } from '../../../shared/smart-grid/smart-grid.models';

@Component({
  selector: 'app-integration-type-management',
  templateUrl: './integration-type-management.component.html',
  styleUrls: ['./integration-type-management.component.scss']
})
export class IntegrationTypeManagementComponent implements OnInit {
  // Data
  integrationTypes: any = { rows: [], totalRows: 0 };
  
  // Form
  integrationTypeForm: FormGroup;
  currentIntegrationType: IntegrationType | null = null;
  isEditing = false;
  
  // Modals
  private modalRef?: NgbModalRef;
  @ViewChild('integrationTypeModal') integrationTypeModal!: TemplateRef<any>;
  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  
  // Delete
  integrationTypeToDelete: IntegrationType | null = null;
  deleteUsageCount: number = 0;
  
  // Categories for dropdown
  categories = [
    { value: 'Import', label: 'Import' },
    { value: 'Export', label: 'Export' },
    { value: 'Sync', label: 'Sync' }
  ];
  
  // Grid columns
  columns: GridColumn<any>[] = [];

  constructor(
    private systemIntegrationTypeService: SystemIntegrationTypeService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modalService: NgbModal
  ) {
    this.integrationTypeForm = this.formBuilder.group({
      integrationTypeID: [0],
      typeCode: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(/^[A-Za-z0-9_-]+$/)]],
      typeName: ['', [Validators.required, Validators.maxLength(100)]],
      category: ['Import', Validators.required],
      adapterClassName: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      configSchema: [''],
      requiresAuthentication: [false],
      supportsBatchProcessing: [false],
      supportsScheduling: [false],
      hasPreconfiguredMapping: [false], // True for adapters with hardcoded mappings (e.g., NXT GraphQL)
      isActive: [true]
    });
  }

  ngOnInit() {
    this.buildColumns();
    this.loadIntegrationTypes();
  }

  get f() {
    return this.integrationTypeForm.controls;
  }

  /**
   * Build grid columns
   */
  buildColumns() {
    this.columns = [
      {
        field: 'integrationTypeID',
        title: this.translateService.instant('SystemAdmin.integrationTypeID'),
        width: 80,
        sortable: true,
        filterable: true
      },
      {
        field: 'typeName',
        title: this.translateService.instant('SystemAdmin.typeName'),
        width: 180,
        sortable: true,
        filterable: true
      },
      {
        field: 'typeCode',
        title: this.translateService.instant('SystemAdmin.typeCode'),
        width: 120,
        sortable: true,
        filterable: true
      },
      {
        field: 'category',
        title: this.translateService.instant('SystemAdmin.category'),
        width: 100,
        sortable: true,
        filterable: true
      },
      {
        field: 'adapterClassName',
        title: this.translateService.instant('SystemAdmin.adapterClassName'),
        width: 220,
        sortable: true,
        filterable: true
      },
      {
        field: 'isActive',
        title: this.translateService.instant('General.active'),
        width: 80,
        sortable: true,
        filterable: true
      },
      {
        field: 'activeIntegrationCount',
        title: this.translateService.instant('SystemAdmin.activeIntegrationCount'),
        width: 100,
        sortable: true,
        filterable: false
      },
      {
        field: 'actions',
        title: this.translateService.instant('General.actions'),
        width: 120,
        sortable: false,
        filterable: false
      }
    ];
  }

  /**
   * Load all integration types
   */
  loadIntegrationTypes() {
    this.systemIntegrationTypeService.getAllIntegrationTypes().subscribe({
      next: (response) => {
        if (response?.success && response.result) {
          this.integrationTypes = {
            rows: response.result,
            totalRows: response.result.length
          };
        } else {
          this.toastService.showApiError(response?.errorMessage, this.translateService.instant('SystemAdmin.errorLoadingIntegrationTypes'));
        }
      },
      error: (error) => {
        console.error('Error loading integration types:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingIntegrationTypes')]
        });
      }
    });
  }

  /**
   * Open modal to create new integration type
   */
  openCreateModal() {
    this.isEditing = false;
    this.currentIntegrationType = null;
    this.integrationTypeForm.reset({
      integrationTypeID: 0,
      category: 'Import',
      requiresAuthentication: false,
      supportsBatchProcessing: false,
      supportsScheduling: false,
      isActive: true
    });
    this.modalRef = this.modalService.open(this.integrationTypeModal, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Open modal to edit integration type
   */
  openEditModal(integrationType: IntegrationType) {
    this.isEditing = true;
    this.currentIntegrationType = integrationType;
    this.integrationTypeForm.patchValue({
      integrationTypeID: integrationType.integrationTypeID,
      typeCode: integrationType.typeCode,
      typeName: integrationType.typeName,
      category: integrationType.category,
      adapterClassName: integrationType.adapterClassName,
      description: integrationType.description || '',
      configSchema: integrationType.configSchema || '',
      requiresAuthentication: integrationType.requiresAuthentication,
      supportsBatchProcessing: integrationType.supportsBatchProcessing,
      supportsScheduling: integrationType.supportsScheduling,
      isActive: integrationType.isActive
    });
    this.modalRef = this.modalService.open(this.integrationTypeModal, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Save integration type (create or update)
   */
  saveIntegrationType() {
    if (this.integrationTypeForm.invalid) {
      Object.keys(this.integrationTypeForm.controls).forEach(key => {
        this.integrationTypeForm.get(key)?.markAsTouched();
      });
      return;
    }

    const formValue = this.integrationTypeForm.value;
    const request: SaveIntegrationTypeRequest = {
      integrationTypeID: this.isEditing ? formValue.integrationTypeID : undefined,
      typeCode: formValue.typeCode,
      typeName: formValue.typeName,
      category: formValue.category,
      adapterClassName: formValue.adapterClassName,
      description: formValue.description || undefined,
      configSchema: formValue.configSchema || undefined,
      requiresAuthentication: formValue.requiresAuthentication,
      supportsBatchProcessing: formValue.supportsBatchProcessing,
      supportsScheduling: formValue.supportsScheduling,
      hasPreconfiguredMapping: formValue.hasPreconfiguredMapping || false,
      isActive: formValue.isActive
    };

    const operation = this.isEditing
      ? this.systemIntegrationTypeService.updateIntegrationType(formValue.integrationTypeID, request)
      : this.systemIntegrationTypeService.createIntegrationType(request);

    operation.subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant(
              this.isEditing ? 'SystemAdmin.integrationTypeUpdated' : 'SystemAdmin.integrationTypeCreated'
            )]
          });
          this.closeModal();
          this.loadIntegrationTypes();
        } else {
          const errorMessage = response?.errorMessage?.[0]?.message || 
            this.translateService.instant('General.operationFailed');
          this.toastService.show({ type: 'error', data: [errorMessage] });
        }
      },
      error: (error) => {
        console.error('Error saving integration type:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('General.operationFailed')]
        });
      }
    });
  }

  /**
   * Open delete confirmation modal
   */
  openDeleteModal(integrationType: IntegrationType) {
    this.integrationTypeToDelete = integrationType;
    
    // Check usage count before allowing delete
    this.systemIntegrationTypeService.getUsageCount(integrationType.integrationTypeID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.deleteUsageCount = response.result || 0;
          this.modalRef = this.modalService.open(this.deleteConfirmModal, { backdrop: 'static' });
        }
      },
      error: (error) => {
        console.error('Error checking usage count:', error);
        this.deleteUsageCount = 0;
        this.modalRef = this.modalService.open(this.deleteConfirmModal, { backdrop: 'static' });
      }
    });
  }

  /**
   * Confirm delete integration type
   */
  confirmDelete() {
    if (!this.integrationTypeToDelete) return;

    this.systemIntegrationTypeService.deleteIntegrationType(this.integrationTypeToDelete.integrationTypeID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.integrationTypeDeleted')]
          });
          this.closeModal();
          this.loadIntegrationTypes();
        } else {
          const errorMessage = response?.errorMessage?.[0]?.message || 
            this.translateService.instant('General.operationFailed');
          this.toastService.show({ type: 'error', data: [errorMessage] });
        }
      },
      error: (error) => {
        console.error('Error deleting integration type:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('General.operationFailed')]
        });
      }
    });
  }

  /**
   * Close modal
   */
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = undefined;
    }
    this.integrationTypeForm.reset();
    this.currentIntegrationType = null;
    this.integrationTypeToDelete = null;
  }

  /**
   * Get category badge class
   */
  getCategoryClass(category: string): string {
    switch (category) {
      case 'Import': return 'badge-soft-success';
      case 'Export': return 'badge-soft-primary';
      case 'Sync': return 'badge-soft-info';
      default: return 'badge-soft-secondary';
    }
  }
}
